import { Suspense, lazy, useEffect } from "react";
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HeroUIProvider } from "@heroui/react";
import { TooltipProvider } from "@/components/shared/ui/tooltip";

import { Toaster } from "@/components/shared/ui/toaster";
import { toast } from "@/hooks/use-toast";
import { Footer } from "@/features/user/components/organisms/Footer";
import { Navbar } from "@/features/user/components/organisms/Navbar";
import { SmartAssistantSheet } from "@/features/storefront/components/AiAssistant/SmartAssistantSheet";
import { useUIStore } from "@/stores/useUIStore";
import { publicRoutes, adminRoutes } from '@/config/routes';
import { queryClient } from "@/lib/queryClient";
import { RegionalConfigProvider } from "@/contexts/RegionalConfigContext";
import { ShippingConfigProvider } from "@/contexts/ShippingConfigContext";
import { AnalyticsLoader } from "@/integrations/analytics/AnalyticsLoader";
import { AppConfigProvider } from "@/contexts/AppConfigContext";

import { isManageSubdomain } from "@/api/core/authContext";
import LoaderMatch from "./components/uiverse/LoaderMatch";
import { SmoothScroll } from "@/components/shared/SmoothScroll";

const AuthModal = lazy(() => import("@/features/user/components/auth"));

function AppContent() {
  const { isAuthModalOpen, closeAuthModal, authModalMode, openAuthModal } = useUIStore();
  const isManage = isManageSubdomain();
  // Seleccionamos las rutas basadas en el subdominio
  const activeRoutes = isManage ? adminRoutes : publicRoutes;
  // El layout de la tienda solo se muestra en el dominio principal
  const shouldShowStoreLayout = !isManage;

  // Detectar redirección de confirmación de email o recuperación de contraseña
  useEffect(() => {
    const handleAuthRedirect = () => {
      const hash = window.location.hash;
      const query = new URLSearchParams(window.location.search);
      const type = query.get('type') || (hash.includes('type=recovery') ? 'recovery' : hash.includes('type=signup') ? 'signup' : null);

      if (type === 'recovery') {
        const hashParams = new URLSearchParams(hash.replace('#', '?'));
        const token = hashParams.get('access_token');
        openAuthModal('reset-password', token || undefined);
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (type === 'signup') {
        openAuthModal('login');
        // Opcional: Mostrar mensaje de éxito si se confirma
        toast.success("¡Cuenta confirmada! Por favor inicia sesión.");
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    handleAuthRedirect();
  }, [openAuthModal]);

  return (
    <>
      <AnalyticsLoader />
      {shouldShowStoreLayout && (
        <>
          <Navbar />
          <SmartAssistantSheet />
        </>
      )}

      {isAuthModalOpen && (
        <Suspense fallback={null}>
          <AuthModal
            isOpen={isAuthModalOpen}
            onOpenChange={(open) => !open && closeAuthModal()}
            defaultMode={authModalMode}
          />
        </Suspense>
      )}

      <main className="flex-grow flex flex-col w-full">
        <Suspense fallback={<LoaderMatch />}>
          <Routes>
            {activeRoutes.map((route: import('@/config/routes').RouteConfig) => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
                index={route.index}
              />
            ))}
          </Routes>
        </Suspense>
      </main>

      {shouldShowStoreLayout && <Footer />}
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <AppConfigProvider>
          <RegionalConfigProvider>
            <ShippingConfigProvider>
              <HeroUIProvider>
                <TooltipProvider>
                  <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                    <SmoothScroll>
                      <AppContent />
                    </SmoothScroll>
                    <Toaster />
                  </BrowserRouter>
                </TooltipProvider>
              </HeroUIProvider>
            </ShippingConfigProvider>
          </RegionalConfigProvider>
        </AppConfigProvider>
      </HelmetProvider>
    </QueryClientProvider >
  );
}

export default App;
